import { useState, useEffect, useContext } from 'react';
import { Bell, BellOff, Info } from 'lucide-react';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return new Uint8Array(rawData.length).map((_, i) => rawData.charCodeAt(i));
}

const PushNotificationToggle = () => {
  const { language } = useContext(AuthContext);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [loading, setLoading] = useState(true);

  const t = (es, ca) => language === 'ca' ? ca : es;

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setPushSupported(supported);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/push/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsSubscribed(res.data.subscribed);
    } catch (e) {
      // Ignore
    }
    setLoading(false);
  };

  const subscribe = async () => {
    if (!pushSupported) {
      // Show helpful message for unsupported browsers (iOS Safari not standalone)
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (isIOS) {
        toast.info(t(
          'Para recibir notificaciones en iPhone, añade la app a tu pantalla de inicio primero (botón compartir → Añadir a pantalla de inicio)',
          'Per rebre notificacions a l\'iPhone, afegeix l\'app a la pantalla d\'inici primer (botó compartir → Afegir a pantalla d\'inici)'
        ), { duration: 6000 });
      } else {
        toast.error(t(
          'Tu navegador no soporta notificaciones push',
          'El teu navegador no suporta notificacions push'
        ));
      }
      return;
    }

    setLoading(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error(t(
          'Necesitas permitir las notificaciones en tu navegador',
          'Necessites permetre les notificacions al teu navegador'
        ));
        setLoading(false);
        return;
      }

      const registration = await navigator.serviceWorker.register('/sw-push.js');
      await navigator.serviceWorker.ready;

      const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });

      const token = localStorage.getItem('token');
      await axios.post(`${API}/push/subscribe`, subscription.toJSON(), {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      setIsSubscribed(true);
      toast.success(t(
        'Notificaciones activadas. Te escribiremos cada mañana a las 9h.',
        'Notificacions activades. T\'escriurem cada matí a les 9h.'
      ));
    } catch (error) {
      console.error('Push subscription error:', error);
      toast.error(t('Error al activar notificaciones', 'Error en activar notificacions'));
    }
    setLoading(false);
  };

  const unsubscribe = async () => {
    setLoading(true);
    try {
      if (pushSupported) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          const subscription = await registration.pushManager.getSubscription();
          if (subscription) {
            await subscription.unsubscribe();
          }
        }
      }

      const token = localStorage.getItem('token');
      await axios.delete(`${API}/push/unsubscribe`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setIsSubscribed(false);
      toast.success(t('Notificaciones desactivadas', 'Notificacions desactivades'));
    } catch (error) {
      console.error('Push unsubscribe error:', error);
    }
    setLoading(false);
  };

  // Always show the button (even if push not supported, we show a helpful message on click)
  return (
    <button
      data-testid="push-notification-toggle"
      onClick={isSubscribed ? unsubscribe : subscribe}
      disabled={loading}
      className={`
        flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium
        transition-all duration-300
        ${isSubscribed
          ? 'bg-violet-500/20 text-violet-300 border border-violet-400/30 hover:bg-violet-500/30'
          : 'bg-slate-800/60 text-gray-400 border border-slate-700 hover:border-teal-400/30 hover:text-teal-300'
        }
        ${loading ? 'opacity-50 cursor-wait' : ''}
      `}
    >
      {isSubscribed ? (
        <>
          <Bell className="w-4 h-4 fill-current" />
          <span>{t('Notificaciones activas', 'Notificacions actives')}</span>
        </>
      ) : (
        <>
          <BellOff className="w-4 h-4" />
          <span>{t('Activar recordatorio diario', 'Activar recordatori diari')}</span>
        </>
      )}
    </button>
  );
};

export default PushNotificationToggle;
